<template>
  <div v-if="isLoading" class="page-layout">
    <PageLoader />
  </div>
  <router-view v-else />

  <!-- Development onboarding reset tool (only visible in development mode) -->
  <DevResetOnboarding v-if="isDevelopment" />
</template>

<script>
import PageLoader from "@/components/PageLoader.vue";
import DevResetOnboarding from "@/components/onboarding/DevResetOnboarding.vue";
import { useAuth0 } from "@auth0/auth0-vue";
import { computed } from "vue";

import { useWorkSpaceStore } from "@/stores/workspace-store";
import { useAppStore } from "@/stores/app-store";
import { mapStores } from "pinia";

import { getUser } from "@/services/workspace.service";
import { analytics } from "@/services/analytics.service";

export default {
  components: {
    PageLoader,
    DevResetOnboarding,
  },
  setup() {
    const { isLoading, isAuthenticated } = useAuth0();

    // Check if we're in development mode
    const isDevelopment = computed(() => {
      return import.meta.env.VITE_ENV !== "production";
    });

    return {
      isLoading,
      isAuthenticated,
      isDevelopment,
    };
  },
  computed: {
    ...mapStores(useAppStore),
    ...mapStores(useWorkSpaceStore),
  },
  data() {
    return {
      greeting: "Hello from parent",
      testLoading: true,
      hasInitialized: false, // Prevent double initialization
    };
  },

  // Watch for auth to complete before making API calls.
  // This replaces mounted() which fires immediately, causing API calls
  // to fail when the SDK is still processing the callback.
  watch: {
    isLoading: {
      immediate: true,
      async handler(loading) {
        // Only proceed when loading is complete AND user is authenticated AND not already initialized
        if (!loading && this.isAuthenticated && !this.hasInitialized) {
          this.hasInitialized = true;
          await this.initializeAuthenticatedApp();
        }
      },
    },
  },

  methods: {
    async initializeAuthenticatedApp() {
      console.log("[App] Auth complete, initializing authenticated app...");

      try {
        // Execute these requests in parallel for better performance
        const [userResponse] = await Promise.all([
          this.loadUserData(),
          this.loadSubscriptionData(),
          // Workspace data is loaded automatically when the store is created
        ]);

        // After core data is loaded, set up third-party integrations
        if (userResponse && userResponse.data) {
          this.setupAnalytics(userResponse.data);
        }
      } catch (error) {
        console.error("Error during application initialization:", error);
      }
    },

    async loadUserData() {
      try {
        const userResponse = await getUser();
        if (!userResponse.error) {
          this.appStore.user = userResponse.data;
          return userResponse;
        }
      } catch (error) {
        console.error("Failed to load user data:", error);
      }
    },

    async loadSubscriptionData() {
      try {
        await this.workspaceStore.fetchSubscriptionStatus();

        // Since we're not in a free plan, also check file upload limits
        if (!this.workspaceStore.isSubscribedState) {
          await this.workspaceStore.checkDailyFileUploadLimit();
        }
      } catch (error) {
        console.error("Failed to load subscription data:", error);
      }
    },

    setupAnalytics(userData) {
      // Intercom setup
      console.log("User information sending to Intercom:", userData);
      globalThis.Intercom("boot", {
        api_base: "https://api-iam.intercom.io",
        app_id: "bkyv518m",
        user_id: userData.userUUID,
        name: userData.name,
        email: userData.email,
      });

      // PostHog setup
      // The store keeps the plan under `subscription` (there is no
      // `subscriptionStatus`, which made every user read as "free").
      const subscriptionStatus = this.workspaceStore.subscription;
      analytics.identify(userData.userUUID, {
        email: userData.email,
        name: userData.name,
        created_at: userData.createdAt,
        subscription_tier:
          subscriptionStatus?.currentPlanName?.toLowerCase() || "free",
        billing_cycle: subscriptionStatus?.billingCycle || null,
        onboarding_completed: userData.onboardingCompleted || false,
        primary_purpose: userData.primaryPurpose || null,
        collaboration_type: userData.collaborationType || null,
      });

      // Fire `signed_up` exactly once, only for a freshly-created account.
      // setupAnalytics runs on every login, so we guard on (a) createdAt being
      // recent (a returning login — even on a new device — has an old createdAt)
      // and (b) a per-user localStorage flag so reloads/multi-tab don't
      // double-count. This is the funnel endpoint for the canary gate; source
      // attribution comes from the person's $initial_utm_* props.
      try {
        const createdMs = userData.createdAt
          ? new Date(userData.createdAt).getTime()
          : 0;
        const isNewAccount =
          createdMs > 0 && Date.now() - createdMs < 30 * 60 * 1000;
        const dedupeKey = `as_signed_up_${userData.userUUID}`;
        if (isNewAccount && !localStorage.getItem(dedupeKey)) {
          analytics.signedUp({
            created_at: userData.createdAt,
            primary_purpose: userData.primaryPurpose || null,
          });
          localStorage.setItem(dedupeKey, "1");
        }
      } catch (e) {
        console.error("signed_up tracking failed:", e);
      }

      // Set workspace group for PostHog
      const activeWorkspaceUUID = this.workspaceStore.activeWorkspaceUUID;
      const activeWorkspace = this.workspaceStore.workspaces?.find(
        (w) => w.workspaceUUID === activeWorkspaceUUID,
      );
      if (activeWorkspaceUUID && activeWorkspace) {
        analytics.setWorkspaceGroup(activeWorkspaceUUID, {
          name: activeWorkspace.name,
          member_count: activeWorkspace.members?.length || 1,
        });
      }
    },
  },
};
</script>
